import { ChevronDown } from 'lucide-react';
import Button from '../common/Button';
import React, { useRef, useEffect, useState } from 'react';
import { RSS_SOURCE_NAMES } from '../../config/rssSources';

interface FilterBarProps {
  sortBy: string;
  onSortChange: (val: string) => void;
  selectedSources: string[];
  onSourceChange: (vals: string[]) => void;
  onClearFilters: () => void;
}

const SORT_OPTIONS = ['Recent', 'Trending'] as const;

const SOURCE_OPTIONS = RSS_SOURCE_NAMES;

const MultiselectDropdown: React.FC<{
  label: string;
  selected: string[];
  options: readonly string[];
  onChange: (vals: string[]) => void;
}> = ({ label, selected, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const toggleOption = (opt: string) => {
    const updated = selected.includes(opt)
      ? selected.filter(o => o !== opt)
      : [...selected, opt];
    onChange(updated);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative inline-block" ref={ref}>
      <Button variant="outline" size="sm" onClick={() => setOpen(!open)}>
        {label} <ChevronDown className="w-4 h-4 inline ml-1" />
      </Button>

      {open && (
        <div className="absolute z-10 mt-2 w-56 bg-white border border-gray-200 rounded-md shadow-md max-h-64 overflow-y-auto p-2">
          {options.map(opt => (
            <label
              key={opt}
              className="flex items-center gap-2 px-2 py-1 cursor-pointer hover:bg-gray-100"
            >
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                onChange={() => toggleOption(opt)}
                className="accent-blue-500"
              />
              <span>{opt}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const SingleSelectDropdown: React.FC<{
  label: string;
  value: string | null;
  options: readonly string[];
  onChange: (val: string) => void;
}> = ({ label, value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative inline-block" ref={ref}>
      <Button variant="outline" size="sm" onClick={() => setOpen(!open)}>
        {label}: {value || 'Select'} <ChevronDown className="w-4 h-4 inline ml-1" />
      </Button>

      {open && (
        <div className="absolute z-10 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-md">
          {options.map(opt => (
            <div
              key={opt}
              className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
              onClick={() => {
                onChange(opt);
                setOpen(false);
              }}
            >
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const FilterBar: React.FC<FilterBarProps> = ({
  sortBy,
  onSortChange,
  selectedSources,
  onSourceChange,
  onClearFilters,
}) => {
  return (
    <div className="flex flex-wrap items-center gap-3 mb-4">
      <SingleSelectDropdown
        label="Sort by"
        value={sortBy}
        options={SORT_OPTIONS}
        onChange={onSortChange}
      />

      <MultiselectDropdown
        label="Source"
        selected={selectedSources}
        options={SOURCE_OPTIONS}
        onChange={onSourceChange}
      />

      <Button variant="outline" size="sm" onClick={onClearFilters}>
        Clear Filters
      </Button>
    </div>
  );
};

export default FilterBar;